import { BuildingId, EmployeeId, OrgId, SalePointId, SchoolyearId } from 'app/common/types/id'
import { getFilterSalePoints } from 'app/filters/_api/getFilterSalePoints'
import { getFilterEmployees } from 'app/filters/_api/getFilterEmployees'
import { getFilterOrgs } from 'app/filters/_api/getFilterOrgs'
import { getFilterOrgTypes } from 'app/filters/_api/getFilterOrgTypes'
import { getFilterBuildings } from 'app/filters/_api/getFilterBuildings'
import { getFilterSchoolYears } from 'app/filters/_api/getFilterSchoolYears'
import { saleTypes } from 'app/filters/_static/saleTypes'
import type { SaleTypeItem } from 'app/filters/_static/saleTypes'
import { foodModes } from 'app/filters/_static/foodModes'
import type { FoodModeItem } from 'app/filters/_static/foodModes'
import { customerTypes } from 'app/filters/_static/customerTypes'
import { mailItems } from 'app/filters/_static/mailItems'
import type { MailItem } from 'app/filters/_static/mailItems'
import type { CustomerTypeItem } from 'app/filters/_static/customerTypes'
import { OrgType } from 'app/dataTypes/org/types'

import { useFilterStore } from './store'
import { FilterValues } from './store/types'

let handleApply: (state: FilterValues) => void = () => {}

/**
 * Обновление списка заведений в зависимости от выбранного типа заведения
 */
async function updateOrgs(orgType?: OrgType) {
  const store = useFilterStore.getState()

  store.setLoading('orgs', true)
  store.setDraft({ orgIds: undefined, buildingIds: undefined })
  store.setOptions({ orgs: [], buildings: [] })

  const orgs = await getFilterOrgs(orgType ? { orgType } : {})

  store.setLoading('orgs', false)
  store.setOptions({ orgs })
}

/**
 * Обновление списка корпусов в зависимости от выбранных заведений
 * (корпуса доступны только при выборе ровно одного заведения)
 */
async function updateBuildings(orgIds?: Array<OrgId>) {
  const store = useFilterStore.getState()

  store.setLoading('buildings', true)
  store.setDraft({ buildingIds: undefined })
  store.setOptions({ buildings: [] })

  const orgId = orgIds?.length === 1 ? orgIds[0] : undefined

  const buildings = orgId ? await getFilterBuildings(orgId) : []

  store.setLoading('buildings', false)
  store.setOptions({ buildings })
}

/**
 * Обновление списка учебных лет в зависимости от выбранных заведений
 * (учебные годы доступны только при выборе ровно одного заведения)
 */
async function updateSchoolYears(orgIds?: Array<OrgId>) {
  const store = useFilterStore.getState()

  const orgId = orgIds?.length === 1 ? orgIds[0] : undefined

  if (!orgId) {
    store.setDraft({ schoolYearId: undefined })
    store.setOptions({ schoolYears: [] })
    return
  }

  store.setLoading('schoolYears', true)
  store.setDraft({ schoolYearId: undefined })
  store.setOptions({ schoolYears: [] })

  const schoolYears = await getFilterSchoolYears({ orgId })

  // Дефолт — текущий учебный год, а если current нет ни у одного —
  // первый при обратной сортировке (последний календарный год)
  const defaultYear =
    schoolYears.find((year) => year.isCurrent) ??
    [...schoolYears].sort((a, b) => b.title.localeCompare(a.title))[0]

  store.setLoading('schoolYears', false)
  store.setOptions({ schoolYears })

  if (defaultYear) {
    store.setDefaults({ schoolYearId: defaultYear.id })
    store.resetDraftByKey('schoolYearId')
    store.applyDefaultsByKey('schoolYearId')
  }
}

/**
 * Обновление списка точек продаж в зависимости от выбранных заведений
 * (точки продаж доступны только при выборе ровно одного заведения)
 */
async function updateSalePoints(orgIds?: Array<OrgId>) {
  const store = useFilterStore.getState()

  const orgId = orgIds?.length === 1 ? orgIds[0] : undefined

  if (!orgId) {
    store.setDraft({ salePointIds: undefined })
    store.setOptions({ salePoints: [] })
    return
  }

  store.setLoading('salePoints', true)
  store.setDraft({ salePointIds: undefined })
  store.setOptions({ salePoints: [] })

  const salePoints = await getFilterSalePoints(orgId)

  store.setLoading('salePoints', false)
  store.setOptions({ salePoints })
}

/**
 * Обновление списка сотрудников в зависимости от выбранных заведений и типа покупателя
 * (сотрудники доступны только при выборе ровно одного заведения
 * и типа покупателя «Сотрудники»)
 */
async function updateEmployees(orgIds?: Array<OrgId>, customerTypeIds?: Array<CustomerTypeItem['id']>) {
  const store = useFilterStore.getState()

  const orgId = orgIds?.length === 1 ? orgIds[0] : undefined
  const isEmployees = customerTypeIds?.length === 1 && customerTypeIds[0] === 'employee'

  if (!orgId || !isEmployees) {
    store.setDraft({ employeeIds: undefined })
    store.setOptions({ employees: [] })
    return
  }

  store.setLoading('employees', true)
  store.setDraft({ employeeIds: undefined })
  store.setOptions({ employees: [] })

  const items = await getFilterEmployees({ orgId, customerType: 'employee' })

  const employees = items.map(({ id, name }) => ({ id, title: name.toString() }))

  store.setLoading('employees', false)
  store.setOptions({ employees })
}

/**
 * 🌵 Начальная инициализация
 */
async function initialize({
  external,
  onApply,
}: {
  external?: FilterValues
  onApply?: (state: FilterValues) => void
} = {}) {
  if (onApply) handleApply = onApply
  const store = useFilterStore.getState()
  store.setReady(false)
  store.setOptions({})

  store.setDefaults({ orgType: OrgType.School })

  store.setLoading('orgTypes', true)

  const orgTypes = await getFilterOrgTypes()

  store.setLoading('orgTypes', false)

  store.setOptions({ orgTypes })
  store.setOptions({ saleTypes })
  store.setOptions({ foodModes })
  store.setOptions({ customerTypes })
  store.setOptions({ mailItems })

  if (external) store.setDraft(external)
  else store.resetDraft()

  await updateOrgs(store.draft.orgType)
  await updateBuildings(store.draft.orgIds)
  await updateSchoolYears(store.draft.orgIds)
  await updateSalePoints(store.draft.orgIds)
  await updateEmployees(store.draft.orgIds, store.draft.customerTypeIds)

  store.applyDraft()

  store.setReady(true)
}

/**
 * 🌵 Выбор типа заведения (одиночный)
 */
async function selectDraftOrgType(orgType?: OrgType) {
  const store = useFilterStore.getState()

  store.setDraft({ orgType })

  await updateOrgs(orgType)
}

/**
 * 🌵 Выбор заведений (множественный)
 */
async function selectDraftOrgIds(orgIds?: Array<OrgId>) {
  const store = useFilterStore.getState()

  store.setDraft({ orgIds })

  await updateBuildings(orgIds)
  await updateSchoolYears(orgIds)
  await updateSalePoints(orgIds)
  await updateEmployees(orgIds, useFilterStore.getState().draft.customerTypeIds)
}

/**
 * 🌵 Выбор учебного года (одиночный)
 */
async function selectDraftSchoolYearId(schoolYearId?: SchoolyearId) {
  const store = useFilterStore.getState()

  store.setDraft({ schoolYearId })
}

/**
 * 🌵 Выбор корпусов (множественный)
 */
async function selectDraftBuildingIds(buildingIds?: Array<BuildingId>) {
  const store = useFilterStore.getState()

  store.setDraft({ buildingIds })
}

/**
 * 🌵 Выбор типа продаж (множественный)
 * Режим питания доступен только при выбранном «Комплексное питание»;
 * при смене типа продаж выбранные режимы сбрасываются
 */
function selectDraftSaleTypeIds(saleTypeIds?: Array<SaleTypeItem['id']>) {
  const store = useFilterStore.getState()

  store.setDraft({ saleTypeIds })

  if (!saleTypeIds?.includes('complex')) {
    store.setDraft({ foodModeIds: undefined })
  }
}

/**
 * 🌵 Выбор режима питания (множественный)
 */
function selectDraftFoodModeIds(foodModeIds?: Array<FoodModeItem['id']>) {
  const store = useFilterStore.getState()

  store.setDraft({ foodModeIds })
}

/**
 * 🌵 Выбор периода времени [dateFrom, dateTo]
 */
function selectDraftPeriod(period?: [string, string]) {
  const store = useFilterStore.getState()

  store.setDraft({ period })
}

/**
 * 🌵 Выбор точек продаж (множественный)
 */
function selectDraftSalePointIds(salePointIds?: Array<SalePointId>) {
  const store = useFilterStore.getState()

  store.setDraft({ salePointIds })
}

/**
 * 🌵 Выбор типа покупателя (множественный)
 */
async function selectDraftCustomerTypeIds(customerTypeIds?: Array<CustomerTypeItem['id']>) {
  const store = useFilterStore.getState()

  store.setDraft({ customerTypeIds })

  await updateEmployees(store.draft.orgIds, customerTypeIds)
}

/**
 * 🌵 Выбор сотрудников (множественный)
 */
function selectDraftEmployeeIds(employeeIds?: Array<EmployeeId>) {
  const store = useFilterStore.getState()

  store.setDraft({ employeeIds })
}

/**
 * 🌵 Выбор режима отправки отчета на email (одиночный)
 */
function selectDraftMail(mail?: MailItem['id']) {
  const store = useFilterStore.getState()

  store.setDraft({ mail })
}

/**
 * 🌵 Применение фильтров
 */
function apply() {
  const store = useFilterStore.getState()
  store.applyDraft()
  handleApply(store.applied)
}

/**
 * 🌵 Сброс всех фильтров на значения по умолчанию
 */
async function resetToDefaults() {
  const store = useFilterStore.getState()
  store.resetDraft()

  await updateOrgs(store.draft.orgType)
  await updateBuildings(store.draft.orgIds)
  await updateSchoolYears(store.draft.orgIds)
  await updateSalePoints(store.draft.orgIds)
  await updateEmployees(store.draft.orgIds, store.draft.customerTypeIds)

  store.applyDraft()
}

export const filterActions = {
  initialize,
  selectDraftOrgType,
  selectDraftOrgIds,
  selectDraftBuildingIds,
  selectDraftSchoolYearId,
  selectDraftPeriod,
  selectDraftSaleTypeIds,
  selectDraftFoodModeIds,
  selectDraftCustomerTypeIds,
  selectDraftSalePointIds,
  selectDraftEmployeeIds,
  selectDraftMail,

  apply,
  resetToDefaults,
}
